(function (window, document) {
  'use strict';

  var instances = [];
  var isBound = false;
  var layoutQueued = false;

  function isMobileViewport() {
    return !!(window.matchMedia && window.matchMedia('(max-width: 734px)').matches);
  }

  function getGap(track) {
    if (!track || !window.getComputedStyle) return 24;
    var styles = window.getComputedStyle(track);
    return parseFloat(styles.columnGap || styles.gap || '24') || 24;
  }

  function playFeatureVideo(video) {
    if (!video || typeof video.play !== 'function') return;
    var playPromise = video.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(function () {});
    }
  }

  function syncFeatureVideoPlayback(instance) {
    if (!instance || !instance.track) return;

    var slides = Array.prototype.slice.call(instance.track.querySelectorAll(instance.slideSelector || '.take-drive-page__feature-card'));
    if (!slides.length) return;

    var isMobile = isMobileViewport();
    var activeIndex = Math.max(0, Math.min(Number(instance.currentIndex) || 0, slides.length - 1));

    slides.forEach(function (slide, slideIndex) {
      var video = slide.querySelector('.take-drive-page__feature-video');
      if (!video) return;

      if (!isMobile) {
        playFeatureVideo(video);
        return;
      }

      if (slideIndex === activeIndex) {
        playFeatureVideo(video);
      } else if (!video.paused) {
        video.pause();
      }
    });
  }

  function queueFeatureVideoPlayback(instance) {
    if (!instance) return;
    if (instance.featureVideoFrame) {
      window.cancelAnimationFrame(instance.featureVideoFrame);
    }
    instance.featureVideoFrame = window.requestAnimationFrame(function () {
      instance.featureVideoFrame = 0;
      syncFeatureVideoPlayback(instance);
    });
  }

  function clearStyles(instance) {
    if (!instance) return;
    if (instance.wrap) {
      instance.wrap.style.minHeight = '';
    }
    if (instance.container) {
      instance.container.style.position = '';
      instance.container.style.left = '';
      instance.container.style.right = '';
      instance.container.style.width = '';
    }
    if (instance.track) {
      instance.track.style.paddingLeft = '';
      instance.track.style.paddingRight = '';
      instance.track.style.transform = '';
      instance.track.classList.remove('is-dragging');
    }
    (instance.slides || []).forEach(function (slide) {
      if (!slide) return;
      slide.style.width = '';
      slide.style.flexBasis = '';
      slide.style.flex = '';
    });
    instance.currentIndex = 0;
    instance.step = 0;
    queueFeatureVideoPlayback(instance);
  }

  function applyIndex(instance) {
    if (!instance || !instance.track) return;
    var total = Array.isArray(instance.slides) ? instance.slides.length : 0;
    if (!total) {
      instance.track.style.transform = '';
      queueFeatureVideoPlayback(instance);
      return;
    }
    if (instance.currentIndex < 0) instance.currentIndex = 0;
    if (instance.currentIndex > total - 1) instance.currentIndex = total - 1;
    var offset = instance.currentIndex * instance.step;
    instance.track.style.transform = 'translateX(-' + offset + 'px)';
    queueFeatureVideoPlayback(instance);
  }

  function layoutInstance(instance) {
    if (!instance || !instance.track || !instance.track.isConnected) return;

    instance.slides = Array.prototype.slice.call(instance.track.querySelectorAll(instance.slideSelector));
    if (!instance.slides.length) return;

    if (!isMobileViewport()) {
      clearStyles(instance);
      return;
    }

    var anchor = instance.container.closest('.component-grid-item')
      || instance.container.closest('.component-grid')
      || instance.wrap
      || instance.container.parentElement;
    if (!anchor) return;

    var rect = anchor.getBoundingClientRect();
    var leftSpace = Math.max(0, rect.left);
    var rightSpace = Math.max(0, window.innerWidth - rect.right);
    var gap = getGap(instance.track);
    var cardWidth = Math.max(0, rect.width - gap);

    instance.gap = gap;
    instance.step = cardWidth + gap;

    instance.container.style.position = 'absolute';
    instance.container.style.left = '-' + leftSpace + 'px';
    instance.container.style.right = '-' + rightSpace + 'px';
    instance.container.style.width = 'auto';

    instance.track.style.paddingLeft = leftSpace + 'px';
    instance.track.style.paddingRight = Math.max(0, rightSpace - gap) + 'px';

    instance.slides.forEach(function (slide) {
      slide.style.width = cardWidth + 'px';
      slide.style.flexBasis = cardWidth + 'px';
      slide.style.flex = '0 0 ' + cardWidth + 'px';
    });

    applyIndex(instance);

    window.requestAnimationFrame(function () {
      if (!instance.wrap || !instance.track) return;
      instance.wrap.style.minHeight = instance.track.getBoundingClientRect().height + 'px';
    });
  }

  function layoutAll() {
    instances = instances.filter(function (instance) {
      return !!(instance && instance.track && instance.track.isConnected);
    });
    instances.forEach(layoutInstance);
  }

  function queueLayout() {
    if (layoutQueued) return;
    layoutQueued = true;
    window.requestAnimationFrame(function () {
      layoutQueued = false;
      layoutAll();
    });
  }

  function bindCarousel(container, trackSelector, slideSelector) {
    if (!container || container.getAttribute('data-take-drive-carousel-ready') === 'true') return;

    var track = trackSelector ? container.querySelector(trackSelector) : container;
    var wrap = container.parentElement;
    if (!track || !wrap) return;

    var instance = {
      container: container,
      wrap: wrap,
      track: track,
      slideSelector: slideSelector,
      slides: [],
      currentIndex: 0,
      step: 0,
      gap: 24,
      featureVideoFrame: 0
    };

    var startX = 0;
    var startY = 0;
    var deltaX = 0;
    var deltaY = 0;
    var isDragging = false;
    var isHorizontalDrag = false;
    var suppressClick = false;

    track.addEventListener('touchstart', function (event) {
      if (!isMobileViewport()) return;
      if (!event.touches || !event.touches.length) return;
      instance.slides = Array.prototype.slice.call(track.querySelectorAll(slideSelector));
      if (instance.slides.length < 2) return;

      startX = event.touches[0].clientX;
      startY = event.touches[0].clientY;
      deltaX = 0;
      deltaY = 0;
      isDragging = true;
      isHorizontalDrag = false;
      suppressClick = false;
      track.classList.add('is-dragging');
    }, { passive: true });

    track.addEventListener('touchmove', function (event) {
      if (!isDragging || !event.touches || !event.touches.length) return;

      deltaX = event.touches[0].clientX - startX;
      deltaY = event.touches[0].clientY - startY;

      if (!isHorizontalDrag) {
        if (Math.abs(deltaX) > 8 && Math.abs(deltaX) > Math.abs(deltaY)) {
          isHorizontalDrag = true;
        } else if (Math.abs(deltaY) > 8) {
          return;
        }
      }

      if (!isHorizontalDrag) return;

      event.preventDefault();
      suppressClick = true;

      var total = Array.isArray(instance.slides) ? instance.slides.length : 0;
      var baseOffset = instance.currentIndex * instance.step;
      var maxOffset = Math.max(0, (total - 1) * instance.step);
      var previewOffset = baseOffset - deltaX;

      if (previewOffset < 0) {
        previewOffset = previewOffset * 0.35;
      } else if (previewOffset > maxOffset) {
        previewOffset = maxOffset + (previewOffset - maxOffset) * 0.35;
      }

      track.style.transform = 'translateX(-' + previewOffset + 'px)';
    }, { passive: false });

    function endTouchDrag() {
      if (!isDragging) return;
      isDragging = false;
      track.classList.remove('is-dragging');

      if (isHorizontalDrag) {
        var threshold = Math.max(40, Math.min(80, instance.step * 0.12 || 50));
        if (deltaX <= -threshold && instance.currentIndex < instance.slides.length - 1) {
          instance.currentIndex += 1;
        } else if (deltaX >= threshold && instance.currentIndex > 0) {
          instance.currentIndex -= 1;
        }
      }

      deltaX = 0;
      deltaY = 0;
      isHorizontalDrag = false;
      applyIndex(instance);
    }

    track.addEventListener('touchend', endTouchDrag, { passive: true });
    track.addEventListener('touchcancel', endTouchDrag, { passive: true });

    track.addEventListener('click', function (event) {
      if (!suppressClick) return;
      suppressClick = false;
      event.preventDefault();
      event.stopPropagation();
    }, true);

    container.setAttribute('data-take-drive-carousel-ready', 'true');
    instances.push(instance);
    queueFeatureVideoPlayback(instance);
  }

  function init(configs, root) {
    var scope = root && typeof root.querySelectorAll === 'function' ? root : document;
    (configs || []).forEach(function (config) {
      scope.querySelectorAll(config.selector).forEach(function (container) {
        bindCarousel(container, config.trackSelector || '', config.slideSelector);
      });
    });
    queueLayout();
  }

  function initDefaults(root) {
    init([
      { selector: '.take-drive-page__feature-carousel', trackSelector: '.take-drive-page__feature-track', slideSelector: '.take-drive-page__feature-card' },
      { selector: '.take-drive-page__pathway-carousel', trackSelector: '.take-drive-page__pathway-track', slideSelector: '.take-drive-page__pathway-card' }
    ], root);
  }

  function bindGlobalListeners() {
    if (isBound) return;
    isBound = true;

    window.addEventListener('resize', queueLayout);
    window.addEventListener('orientationchange', queueLayout);
    window.addEventListener('scroll', function () {
      instances.forEach(function (instance) {
        queueFeatureVideoPlayback(instance);
      });
    }, { passive: true });

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', queueLayout);
      window.visualViewport.addEventListener('scroll', queueLayout);
    }

    document.addEventListener('shopify:section:load', function () {
      window.setTimeout(function () {
        initDefaults(document);
      }, 0);
    });
  }

  bindGlobalListeners();

  window.TakeDriveCarousel = window.TakeDriveCarousel || {
    init: init,
    initDefaults: initDefaults,
    refresh: queueLayout
  };
}(window, document));
